import { useEffect, useMemo, useState } from 'react';
import '../styles/dashboard-progress-report.css';
import EditableRecordTable from '../components/EditableRecordTable.jsx';
import DashboardInputLayout from '../components/DashboardInputLayout.jsx';
import DashboardInputToolbar from '../components/DashboardInputToolbar.jsx';
import DashboardFilterPanel from '../components/DashboardFilterPanel.jsx';
import {
  getProgressReports,
  createProgressReport,
  updateProgressReport,
  deleteProgressReport,
  getRisks,
  getProblems,
  getActors,
  getEmployees,
  getScheduleOemRegionPairs,
} from '../api/dashboardApi.js';
import { exportToExcel } from '../utils/exportUtils.js';
import ExportConfirmPopup from '../components/ExportConfirmPopup.jsx';
import { getUserName } from '../../../auth/auth.js';
import { canEdit } from '../../../auth/permissions.js';
import {
  buildOptionsByOemRegion,
  buildLookupMap,
  resolveIdsByName,
  resolveEmployeeIds,
} from '../utils/converters.js';
import { buildSelectOptions, normalizeProgressStatusIcon } from '../utils/progressReportConverters.js';

const BASE_COLUMNS = [
  { key: 'oemRegion', label: 'OEM×対地', type: 'select', width: '180px', required: true },
  { key: 'reportDate', label: '報告日', type: 'date', width: '130px', required: true },
  { key: 'region', label: '領域', type: 'select', width: '130px', required: true },
  { key: 'assignee', label: '担当者', type: 'tags', width: '150px', required: true },
  { key: 'progressStatus', label: '進捗状況', type: 'select', width: '130px', required: true,
    renderDisplay: (val) => {
      // 表記揺れ・旧データも必ず3種の天気アイコンに正規化して表示する
      const display = normalizeProgressStatusIcon(val);
      const styles = {
        '☼': { color: '#e87900', textShadow: '0 0 2px #fbbf24' },
        '☁': { color: '#9ca3af' },
        '☂': { color: '#2563eb' },
      };
      /* istanbul ignore next -- 進捗状況は3種のアイコンに正規化済みで空にならない */
      return <span style={{ fontSize: '22px', ...(styles[display] || {}) }}>{display || ''}</span>;
    },
  },
  { key: 'comment', label: '進捗コメント', type: 'text', width: '360px', required: true },
  { key: 'riskTitle', label: '関連リスク', type: 'tags', width: '200px' },
  { key: 'problemTitle', label: '関連課題', type: 'tags', width: '200px' },
  { key: 'pmoComment', label: 'PMOコメント', type: 'text', width: '300px' },
  { key: 'createdAt', label: '作成日時', type: 'text', readOnly: true },
  { key: 'createdBy', label: '作成者', type: 'text', readOnly: true },
  { key: 'updatedAt', label: '最終更新日時', type: 'text', readOnly: true },
  { key: 'updatedBy', label: '最終更新者', type: 'text', readOnly: true },
];

function createEmptyRow() {
  return {
    reportDate: new Date().toISOString().split('T')[0],
    oemRegion: '',
    region: '',
    assignee: '',
    progressStatus: '☼',
    comment: '',
    riskTitle: '',
    problemTitle: '',
    pmoComment: '',
    __isNew: true,
  };
}

export default function DashboardProgressReportPage() {
  const [rows, setRows] = useState([]);
  const [editingRows, setEditingRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportPopupOpen, setExportPopupOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [regionFilter, setRegionFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState(getUserName());
  const [progressStatusFilter, setProgressStatusFilter] = useState('');
  const [keywordFilter, setKeywordFilter] = useState('');
  const [addSignal, setAddSignal] = useState(0);
  const [riskOptionsByOemRegion, setRiskOptionsByOemRegion] = useState({});
  const [problemOptionsByOemRegion, setProblemOptionsByOemRegion] = useState({});
  const [riskByTitle, setRiskByTitle] = useState(new Map());
  const [problemByTitle, setProblemByTitle] = useState(new Map());
  const [actorByName, setActorByName] = useState(new Map());
  const [actorOptions, setActorOptions] = useState(['']);
  const [employeeByName, setEmployeeByName] = useState(new Map());
  const [employeeOptions, setEmployeeOptions] = useState(['']);
  const [oemRegionOptions, setOemRegionOptions] = useState(['']);
  const [oemRegionLookup, setOemRegionLookup] = useState(new Map());
  const [oemRegionFilter, setOemRegionFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [defaultCreateValues, setDefaultCreateValues] = useState({});
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError('');
        const [data, riskData, problemData, actorData, oemRegionPairs, employeeData] = await Promise.all([
          getProgressReports(),
          getRisks(),
          getProblems(),
          getActors(),
          getScheduleOemRegionPairs(),
          getEmployees(),
        ]);
        if (!active) return;
        setRows(data.map((r) => ({
          ...r,
          progressStatus: normalizeProgressStatusIcon(r.progressStatus),
        })));
        // 関連リスク・関連課題の候補は行のOEM×対地に限定する（別OEM×対地への誤リンク防止）
        setRiskOptionsByOemRegion(buildOptionsByOemRegion(riskData, [
          (item) => item.title,
          (item) => item.noId,
          (item) => item.name,
          (item) => item.id,
        ]));
        setProblemOptionsByOemRegion(buildOptionsByOemRegion(problemData, [
          (item) => item.summary,
          (item) => item.title,
          (item) => item.noId,
          (item) => item.name,
          (item) => item.id,
        ]));

        // Build title→id lookup maps for relation saving
        const riskTitleMap = new Map();
        riskData.forEach((item) => {
          const key = item.title || item.noId || item.name || String(item.id);
          if (key) riskTitleMap.set(key, item.id);
        });
        setRiskByTitle(riskTitleMap);

        const problemTitleMap = new Map();
        problemData.forEach((item) => {
          const key = item.summary || item.title || item.noId || item.name || String(item.id);
          if (key) problemTitleMap.set(key, item.id);
        });
        setProblemByTitle(problemTitleMap);

        setActorByName(buildLookupMap(actorData, 'actor'));
        setActorOptions(['', ...actorData.map((item) => item.actor).filter(Boolean)]);
        setEmployeeByName(buildLookupMap(employeeData, 'user_name', 'employee_id'));
        setEmployeeOptions(['', ...employeeData.map((item) => item.user_name).filter(Boolean)]);
        const oemRegionOpts = [''];
        const oemRegionMap = new Map();
        oemRegionPairs.forEach((p) => {
          if (!oemRegionMap.has(p.label)) {
            oemRegionOpts.push(p.label);
            oemRegionMap.set(p.label, { oemId: p.oem_id, regionId: p.region_id });
          }
        });
        setOemRegionOptions(oemRegionOpts);
        setOemRegionLookup(oemRegionMap);
        setDefaultCreateValues({
          oemId: oemRegionPairs[0]?.oem_id,
          regionId: oemRegionPairs[0]?.region_id,
        });
      } catch (err) {
        if (!active) return;
        setError('進捗報告データの取得に失敗しました');
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, []);

  const columns = useMemo(
    () =>
      BASE_COLUMNS.map((col) => {
        if (col.key === 'assignee') {
          return { ...col, width: /* istanbul ignore next -- この列は BASE_COLUMNS で width を指定済み */ (col.width || '148px'), getOptions: () => employeeOptions };
        }
        if (col.key === 'riskTitle') {
          return {
            ...col,
            width: /* istanbul ignore next -- 同上 */ (col.width || '148px'),
            getOptions: ({ row }) => riskOptionsByOemRegion[row?.oemRegion || ''] || [''],
          };
        }
        if (col.key === 'problemTitle') {
          return {
            ...col,
            width: /* istanbul ignore next -- 同上 */ (col.width || '148px'),
            getOptions: ({ row }) => problemOptionsByOemRegion[row?.oemRegion || ''] || [''],
          };
        }
        if (col.type === 'select') {
          const opts = col.key === 'oemRegion'
            ? oemRegionOptions
            : col.key === 'region'
              ? actorOptions
              : /* istanbul ignore next -- select 列は OEM×対地・領域・進捗状況の3種のみ */ col.key === 'progressStatus'
                // 未登録（空）を許さないため空選択肢は置かない。デフォルトは晴れ
                ? ['☼', '☁', '☂']
                : undefined;
          return { ...col, width: /* istanbul ignore next -- 同上 */ (col.width || '148px'), options: opts };
        }
        return { ...col, width: (col.width || '148px') };
      }),
    [actorOptions, editingRows, employeeOptions, oemRegionOptions, problemOptionsByOemRegion, riskOptionsByOemRegion, rows],
  );

  const filterOptions = useMemo(() => {
    const toOptions = (key) => ['', ...Array.from(new Set(rows.map((row) => row[key]).filter(Boolean)))];
    return {
      oemRegion: oemRegionOptions,
      region: actorOptions.length > 1 ? actorOptions : toOptions('region'),
      assignee: toOptions('assignee'),
      progressStatus: toOptions('progressStatus'),
    };
  }, [actorOptions, oemRegionOptions, rows]);

  const clearFilters = () => {
    setOemRegionFilter('');
    setRegionFilter('');
    setAssigneeFilter('');
    setProgressStatusFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setKeywordFilter('');
  };

  const filteredRows = useMemo(() => {
    const keyword = keywordFilter.trim().toLowerCase();

    return rows.filter((row) => {
      if (oemRegionFilter && row.oemRegion !== oemRegionFilter) return false;
      if (regionFilter && row.region !== regionFilter) return false;
      if (assigneeFilter && !(row.assignee || '').includes(assigneeFilter)) return false;
      if (progressStatusFilter && row.progressStatus !== progressStatusFilter) return false;
      if (dateFromFilter || dateToFilter) {
        const dateValue = row.reportDate;
        if (!dateValue) return false;
        if (dateFromFilter && dateValue < dateFromFilter) return false;
        if (dateToFilter && dateValue > dateToFilter) return false;
      }

      if (keyword) {
        const searchable = [row.comment, row.riskTitle, row.problemTitle, row.pmoComment]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!searchable.includes(keyword)) return false;
      }

      return true;
    });
  }, [assigneeFilter, dateFromFilter, dateToFilter, keywordFilter, oemRegionFilter, progressStatusFilter, regionFilter, rows]);

  const progressReportFilterValues = {
    oemRegion: oemRegionFilter,
    region: regionFilter,
    assignee: assigneeFilter,
    progressStatus: progressStatusFilter,
    date_from: dateFromFilter,
    date_to: dateToFilter,
    keyword: keywordFilter,
  };

  const progressReportFilterFields = useMemo(
    () => [
      {
        key: 'oemRegion',
        label: 'OEM×対地',
        type: 'select',
        options: filterOptions.oemRegion.map((option) => ({ value: option, label: option || 'すべて' })),
      },
      {
        key: 'region',
        label: '領域',
        type: 'select',
        options: filterOptions.region.map((option) => ({ value: option, label: option || 'すべて' })),
      },
      {
        key: 'assignee',
        label: '担当者',
        type: 'select',
        options: employeeOptions.map((option) => ({
          value: option,
          label: option || 'すべて',
        })),
      },
      {
        key: 'progressStatus',
        label: '進捗状況',
        type: 'select',
        options: filterOptions.progressStatus.map((option) => ({ value: option, label: option || 'すべて' })),
      },
      { key: 'date_from', label: '報告日From', type: 'date' },
      { key: 'date_to', label: '報告日To', type: 'date' },
      {
        key: 'keyword',
        label: 'キーワード',
        type: 'text',
        className: 'pm-filter-item--keyword',
        placeholder: '進捗コメント / 関連リスク / 関連課題 / PMOコメント',
      },
    ],
    [employeeOptions, filterOptions.oemRegion, filterOptions.progressStatus, filterOptions.region],
  );

  const handleProgressReportFilterChange = (key, value) => {
    switch (key) {
      case 'oemRegion':
        setOemRegionFilter(value);
        break;
      case 'region':
        setRegionFilter(value);
        break;
      case 'assignee':
        setAssigneeFilter(value);
        break;
      case 'progressStatus':
        setProgressStatusFilter(value);
        break;
      case 'date_from':
        setDateFromFilter(value);
        break;
      case 'date_to':
        setDateToFilter(value);
        break;
      case 'keyword':
        setKeywordFilter(value);
        break;
      /* istanbul ignore next -- filterFields で定義済みキーのみ渡るため到達しない防御 default 分岐 */
      default:
        break;
    }
  };

  const handleExportConfirm = async (scope) => {
    const data = scope === 'filtered' ? filteredRows : rows;
    setExporting(true);
    try {
      await exportToExcel(data, BASE_COLUMNS, '進捗報告');
    } finally {
      setExporting(false);
      setExportPopupOpen(false);
    }
  };

  const buildReportPayload = (row) => {
    const eids = resolveEmployeeIds(row.assignee, employeeByName);
    let oemId = defaultCreateValues.oemId;
    let regionId = defaultCreateValues.regionId;
    /* istanbul ignore else -- OEM×対地は必須かつ選択式なので未解決の値は保存前に弾かれる */
    if (row.oemRegion && oemRegionLookup.has(row.oemRegion)) {
      const resolved = oemRegionLookup.get(row.oemRegion);
      oemId = resolved.oemId;
      regionId = resolved.regionId;
    }
    const riskIds = resolveIdsByName(row.riskTitle, riskByTitle);
    const problemIds = resolveIdsByName(row.problemTitle, problemByTitle);
    const payload = {
      report_date: row.reportDate,
      title: /* istanbul ignore next -- OEM×対地は必須項目なので空にならない */ (row.oemRegion || ''),
      progress_status: normalizeProgressStatusIcon(row.progressStatus),
      progress_comment: row.comment,
      pmo_comment: row.pmoComment,
      risk_ids: riskIds,
      problem_ids: problemIds,
      employee_ids: eids,
      updated_at_iso: row.updated_at_iso ?? null,
    };
    return { payload, oemId, regionId };
  };

  const handlePopupDelete = async (row) => {
    /* istanbul ignore if -- 削除ボタンは既存レコード（id あり）にしか出ない */
    if (!row.id) throw new Error('新規レコードは削除できません');
    await deleteProgressReport(row.id, row.updated_at_iso ?? null);
    const data = await getProgressReports();
    setRows(data.map((r) => ({ ...r, progressStatus: normalizeProgressStatusIcon(r.progressStatus) })));
  };

  const handlePopupSave = async (row) => {
    const { payload, oemId, regionId } = buildReportPayload(row);
    /* istanbul ignore next -- 領域は必須かつ選択式なので未解決の値は保存前に弾かれる */
    const actorId = row.region ? (actorByName.get(row.region) || null) : null;
    const eids = resolveEmployeeIds(row.assignee, employeeByName);
    if (!oemId || !regionId || !actorId || !row.reportDate || !payload.progress_status || !row.comment || eids.length === 0) {
      throw new Error('必須項目が未入力です');
    }
    let savedId;
    try {
      payload.oem_id = oemId;
      payload.region_id = regionId;
      payload.actor_id = actorId;
      if (row.id) {
        await updateProgressReport(row.id, payload);
        savedId = row.id;
      } else {
        const created = await createProgressReport(payload);
        savedId = created?.id;
      }
    } catch (err) {
      if (err.status === 409) {
        throw new Error('他のユーザーによってこのレコードが更新されました。\n入力した内容は画面に残っています。\nOK を押した後、メモなど必要な内容をコピーしてから\nキャンセルで閉じて最新データを確認してください。');
      }
      throw err;
    }
    const data = await getProgressReports();
    setRows(data.map((r) => ({ ...r, progressStatus: normalizeProgressStatusIcon(r.progressStatus) })));
    if (savedId != null) {
      setHighlightedRowId(savedId);
      setTimeout(() => setHighlightedRowId(null), 5000);
    }
  };


  return (
    <>
    <DashboardInputLayout
      inputTabIndex={1}
      toolbar={
        !loading && !error ? (
          <DashboardInputToolbar
            countText={`${filteredRows.length} / ${rows.length} 件を表示`}
            exporting={exporting}
            onAdd={() => setAddSignal((prev) => prev + 1)}
            onExport={() => setExportPopupOpen(true)}
            onToggleFilters={() => setShowFilters((prev) => !prev)}
            addLabel="+ レコード追加"
            showAdd
            showExport
            showFilters={showFilters}
            showFilterToggle
          />
        ) : null
      }
      filters={
        !loading && !error && showFilters ? (
          <DashboardFilterPanel
            fields={progressReportFilterFields}
            onClear={clearFilters}
            onFieldChange={handleProgressReportFilterChange}
            values={progressReportFilterValues}
          />
        ) : null
      }
    >
      {loading && <div>読み込み中...</div>}
      {!loading && error && <div>{error}</div>}
      {!loading && !error && (
      <EditableRecordTable
        addButtonLabel="+ レコード追加"
        columns={columns}
        createEmptyRow={createEmptyRow}
        enableRowPopup
        externalAddSignal={addSignal}
        highlightedRowId={highlightedRowId}
        initialRows={filteredRows}
        onPopupDelete={handlePopupDelete}
        onPopupSave={handlePopupSave}
        onRowsChange={setEditingRows}
        readOnly={!canEdit('ダッシュボード')}
        rowPopupTitle="進捗報告レコード詳細"
        showAddButton={false}
      />
      )}
    </DashboardInputLayout>
    <ExportConfirmPopup
      open={exportPopupOpen}
      onClose={() => setExportPopupOpen(false)}
      onExport={handleExportConfirm}
      filteredCount={filteredRows.length}
      totalCount={rows.length}
      loading={exporting}
      sheetName="進捗報告"
      filterFields={progressReportFilterFields}
      filterValues={progressReportFilterValues}
    />
    </>
  );
}
